import React, { useState } from 'react';
import {
    Modal,
    Button,
    Form,
    Input,
    Select,
    DatePicker,
    Switch,
    AutoComplete,
    Badge,
} from 'antd';
import { FilterOutlined } from '@ant-design/icons';

/**
 * AdvancedFilter - a reusable advanced filter modal for admin pages
 *
 * Props:
 * - fields: Array of field configs (see below)
 * - onApply: (filters) => void
 * - onReset: () => void (optional)
 * - triggerProps: props for the filter button (optional)
 * - badgeCount: number of active filters (optional)
 *
 * Field config example:
 * {
 *   name: 'big_ref_no',
 *   label: 'Big Ref No',
 *   type: 'text' | 'select' | 'autocomplete' | 'date' | 'daterange' | 'switch',
 *   options: [{label, value}], // for select/autocomplete
 *   fetchOptions: (searchText) => Promise<options>, // for async autocomplete
 *   ...other AntD Form.Item/Input props
 * }
 */

const { RangePicker } = DatePicker;

const AdvancedFilter = ({
    fields = [],
    onApply,
    onReset,
    triggerProps = {},
    initialValues = {},
    badgeCount = 0, // NEW PROP
}) => {
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm();
    const [asyncOptions, setAsyncOptions] = useState({});

    // Handle async autocomplete
    const handleAutoComplete = async (field, value) => {
        if (field.fetchOptions) {
            const options = await field.fetchOptions(value);
            setAsyncOptions((prev) => ({ ...prev, [field.name]: options }));
        }
    };

    const handleApply = (values) => {
        setOpen(false);
        onApply && onApply(values);
    };

    const handleReset = () => {
        form.resetFields();
        setAsyncOptions({});
        onReset && onReset();
    };

    // Render field by type
    const renderField = (field) => {
        switch (field.type) {
            case 'text':
                return (
                    <Input
                        {...field.inputProps}
                        placeholder={field.placeholder || field.label}
                    />
                );
            case 'select':
                return (
                    <Select
                        allowClear
                        options={field.options}
                        placeholder={field.placeholder || field.label}
                        {...field.selectProps}
                    />
                );
            case 'autocomplete':
                return (
                    <AutoComplete
                        options={asyncOptions[field.name] || field.options || []}
                        onSearch={(val) => handleAutoComplete(field, val)}
                        placeholder={field.placeholder || field.label}
                        {...field.autoCompleteProps}
                    />
                );
            case 'date':
                return (
                    <DatePicker style={{ width: '100%' }} {...field.datePickerProps} />
                );
            case 'daterange':
                return (
                    <RangePicker style={{ width: '100%' }} {...field.rangePickerProps} />
                );
            case 'switch':
                return <Switch {...field.switchProps} />;
            default:
                return (
                    <Input
                        {...field.inputProps}
                        placeholder={field.placeholder || field.label}
                    />
                );
        }
    };

    return (
        <>
            <Badge count={badgeCount} size="small" offset={[0, 0]}>
                <Button
                    icon={<FilterOutlined />}
                    onClick={() => setOpen(true)}
                    type={badgeCount > 0 ? 'primary' : 'default'}
                    {...triggerProps}
                >
                    Filter
                </Button>
            </Badge>
            <Modal
                title="Advanced Filters"
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleApply}
                    initialValues={initialValues}
                >
                    <div className="grid md:grid-cols-2 gap-4">
                        {fields.map((field) => (
                            <Form.Item
                                key={field.name}
                                name={field.name}
                                label={field.label}
                                valuePropName={
                                    field.type === 'switch' ? 'checked' : 'value'
                                }
                                rules={field.rules || []}
                            >
                                {renderField(field)}
                            </Form.Item>
                        ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button onClick={handleReset} style={{ marginRight: 8 }}>
                            Reset
                        </Button>
                        <Button type="primary" htmlType="submit">
                            Apply
                        </Button>
                    </div>
                </Form>
            </Modal>
        </>
    );
};

export default AdvancedFilter;
